import React, { useState, useEffect } from 'react';

const EditLessonModal = ({ open, onClose, onSave, onDelete, lesson, students = {}, groups = {}, onDeleteClick, updateSaving = false, deleteSaving = false }) => {
  // Функция для поиска slug студента по UUID
  const findStudentSlugByUuid = (uuid) => {
    if (!uuid) return null;
    // Ищем студента, у которого studentId или id совпадает с UUID
    for (const [slug, student] of Object.entries(students)) {
      if (student.studentId === uuid || student.id === uuid) {
        return slug;
      }
    }
    return null;
  };

  const [itemType, setItemType] = useState(lesson?.type || 'lesson');
  const [selectedId, setSelectedId] = useState(() => {
    if (lesson?.groupId) return `group-${lesson.groupId}`;
    if (lesson?.studentId) {
      // Ищем slug студента по UUID
      const studentSlug = findStudentSlugByUuid(lesson.studentId);
      if (studentSlug) {
        return `student-${studentSlug}`;
      }
      // Fallback: если не нашли по UUID, пробуем напрямую
      return `student-${lesson.studentId}`;
    }
    return '';
  });
  const [title, setTitle] = useState(lesson?.title || '');
  const [from, setFrom] = useState(lesson?.from || '09:00');
  const [to, setTo] = useState(lesson?.to || '10:00');
  const [status, setStatus] = useState(lesson?.status || 'in_progress');
  const [allDay, setAllDay] = useState(lesson?.allDay || false);
  const [date, setDate] = useState(lesson?.date || new Date().toDateString());
  const recurringId = lesson?.recurringId;

  // Функция для автоматического обновления времени окончания
  const updateToTime = (newFromTime) => {
    if (!newFromTime || typeof newFromTime !== 'string') return '10:00';
    const parts = newFromTime.split(':').map(Number);
    const hours = Number.isFinite(parts[0]) ? parts[0] : 9;
    const minutes = Number.isFinite(parts[1]) ? parts[1] : 0;
    const endHours = hours + 1;
    return `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (open && lesson) {
      setItemType(lesson.type || 'lesson');
      if (lesson.groupId) {
        setSelectedId(`group-${lesson.groupId}`);
      } else if (lesson.studentId) {
        // Ищем slug студента по UUID
        const studentSlug = findStudentSlugByUuid(lesson.studentId);
        if (studentSlug) {
          setSelectedId(`student-${studentSlug}`);
        } else {
          // Fallback: если не нашли по UUID, пробуем напрямую
          setSelectedId(`student-${lesson.studentId}`);
        }
      } else {
        setSelectedId('');
      }
      setTitle(lesson.title || '');
      setFrom(lesson.from || '09:00');
      setTo(lesson.to || '10:00');
      setStatus(lesson.status || 'in_progress');
      setAllDay(lesson.allDay || false);
      setDate(lesson.date || new Date().toDateString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lesson, students]);

  if (!open || !lesson) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Edit {itemType === 'lesson' ? 'Lesson' : 'Event'}</h3>
        <div className="modal-row">
          <label>Type:</label>
          <select value={itemType} onChange={e => setItemType(e.target.value)}>
            <option value="lesson">Lesson</option>
            <option value="event">Event</option>
          </select>
        </div>
        <div className="modal-row">
          <label>Date:</label>
          <input
            type="date"
            value={new Date(date).toLocaleDateString('en-CA')}
            onChange={e => setDate(new Date(e.target.value).toDateString())}
          />
        </div>
        <div className="modal-row">
          <label>From:</label>
          <input
            type="time"
            value={from}
            onChange={e => {
              const newFrom = e.target.value;
              setFrom(newFrom);
              // Автоматически устанавливаем время окончания на час позже
              setTo(updateToTime(newFrom));
            }}
          />
        </div>
        <div className="modal-row">
          <label>To:</label>
          <input type="time" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        {itemType === 'lesson' ? (
          <div className="modal-row">
            <label>Student/Group:</label>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)}>
              <option value="">Select student or group</option>
              {Object.keys(groups).length > 0 && (
              <optgroup label="Groups">
                  {Object.entries(groups).map(([id, g]) => (
                  <option key={`group-${id}`} value={`group-${id}`}>● {g.name}</option>
                ))}
              </optgroup>
              )}
              {Object.keys(students).length > 0 && (
              <optgroup label="Students">
                  {Object.entries(students).map(([id, s]) => (
                  <option key={`student-${id}`} value={`student-${id}`}>○ {s.name}</option>
                ))}
              </optgroup>
              )}
              {Object.keys(groups).length === 0 && Object.keys(students).length === 0 && (
                <option value="" disabled>Loading students and groups...</option>
              )}
            </select>
          </div>
        ) : (
          <div className="modal-row">
            <label>Title:</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Event title"
            />
          </div>
        )}
        <div className="modal-row">
          <label>Status:</label>
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="in_progress">In Progress</option>
            <option value="done">Done</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="modal-row">
          <label></label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} />
            All day event
          </label>
        </div>
        <div className="modal-actions">
          <button onClick={onClose} disabled={updateSaving || deleteSaving}>Cancel</button>
          <button
            onClick={() => {
              if (onDeleteClick) {
                onDeleteClick(lesson);
              } else {
                onDelete(lesson);
              }
            }}
            disabled={updateSaving || deleteSaving}
            className={deleteSaving ? 'schedule-add-btn-loading' : ''}
          >
            {deleteSaving ? <span className="schedule-add-btn-spinner" aria-hidden /> : 'Delete'}
          </button>
          <button
            onClick={() => {
              console.log('EditLessonModal onSave recurringId:', recurringId);
              // Parse selectedId to determine if it's a student or group
              let studentId = null;
              let groupId = null;

              if (itemType === 'lesson' && selectedId) {
                if (selectedId.startsWith('student-')) {
                  studentId = selectedId.replace('student-', '');
                } else if (selectedId.startsWith('group-')) {
                  groupId = selectedId.replace('group-', '');
                }
              }

              onSave({
                ...lesson,
                type: itemType,
                studentId,
                groupId,
                title,
                date,
                from,
                to,
                status,
                allDay,
                recurringId
              });
            }}
            disabled={itemType === 'lesson' ? !selectedId : !title || updateSaving || deleteSaving}
            className={updateSaving ? 'schedule-add-btn-loading' : ''}
          >
            {updateSaving ? <span className="schedule-add-btn-spinner" aria-hidden /> : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLessonModal;
